import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import type {
	MultiSelectChangeEvent,
	MultiSelectFilterChangeEvent,
} from "@tql/ui-components-react/kendo/dropdowns";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { MultiSelect } from "@tql/ui-components-react/tql/dropdowns";
import {
	plusCircleLine,
	trash_01,
} from "@tql/ui-components-react/untitled/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type {
	BulkNotificationShipment,
	BulkNotificationTarget,
	NotificationType,
	ScopedBulkNotificationDelta,
	UnifiedIndividualNotificationRecipient,
} from "~/shared/schemas/shipment-notification-schema.ts";
import { NotificationRecipientLevel } from "~/shared/schemas/shipment-notification-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import styles from "../../../notifications/components/shipment-notification-modal/shipment-notification-modal.module.css";
import {
	useBulkNotificationRecipients,
	useSaveBulkNotificationRecipients,
} from "../../../notifications/hooks/use-notification-recipients.ts";
import { useNotificationTypes } from "../../../notifications/hooks/use-notification-types.ts";
import {
	isValidEmail,
	MAX_EMAIL_LENGTH,
} from "../../../notifications/utils/notification-recipient-validation.ts";

/** How a notification type is subscribed across the shipments in this bulk tender. */
type ServerTypeState = "none" | "partial" | "all";

type TargetScope = {
	key: string;
	targets: BulkNotificationTarget[];
};

type SubscriberScope = TargetScope & {
	serverState: Record<number, ServerTypeState>;
	customerLevelTypeIds: Set<number>;
};

type SubscriberRow = {
	email: string;
	firstName: string | null;
	lastName: string | null;
	serverState: Record<number, ServerTypeState>;
	scopes: SubscriberScope[];
	isExisting: boolean;
};

type BulkTenderNotificationModalProps = {
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly targets: readonly BulkNotificationTarget[];
};

type RecipientState = {
	firstName: string | null;
	lastName: string | null;
	effectiveTypeIds: Set<number>;
	customerLevelTypeIds: Set<number>;
	isCustomerLevel: boolean;
};

type RecipientAccumulator = {
	firstName: string | null;
	lastName: string | null;
	typeCounts: Map<number, number>;
	scopes: Map<
		string,
		{
			typeCounts: Map<number, number>;
			customerLevelTypeIds: Set<number>;
		}
	>;
};

const createRecipientAccumulator = (
	state: RecipientState,
): RecipientAccumulator => ({
	firstName: state.firstName,
	lastName: state.lastName,
	typeCounts: new Map<number, number>(),
	scopes: new Map(),
});

const getScopeKey = (customerId: number | null): string =>
	customerId == null ? "external" : `customer:${String(customerId)}`;

const groupTargetsByCustomer = (
	targets: readonly BulkNotificationTarget[],
): TargetScope[] => {
	const scopes = new Map<string, TargetScope>();
	for (const target of targets) {
		const key = getScopeKey(target.customerId);
		const scope = scopes.get(key) ?? { key, targets: [] };
		scope.targets.push(target);
		scopes.set(key, scope);
	}
	return [...scopes.values()];
};

const mergeRecipientState = (
	accumulator: RecipientAccumulator,
	state: RecipientState,
	scopeKey: string,
): void => {
	if (accumulator.firstName == null && state.firstName != null) {
		accumulator.firstName = state.firstName;
	}
	if (accumulator.lastName == null && state.lastName != null) {
		accumulator.lastName = state.lastName;
	}
	const scope = accumulator.scopes.get(scopeKey) ?? {
		typeCounts: new Map<number, number>(),
		customerLevelTypeIds: new Set<number>(),
	};
	for (const typeId of state.effectiveTypeIds) {
		accumulator.typeCounts.set(
			typeId,
			(accumulator.typeCounts.get(typeId) ?? 0) + 1,
		);
		scope.typeCounts.set(typeId, (scope.typeCounts.get(typeId) ?? 0) + 1);
	}
	for (const typeId of state.customerLevelTypeIds) {
		scope.customerLevelTypeIds.add(typeId);
	}
	accumulator.scopes.set(scopeKey, scope);
};

const toServerState = (
	typeCounts: ReadonlyMap<number, number>,
	totalShipmentCount: number,
	typeIds: readonly number[],
): Record<number, ServerTypeState> =>
	Object.fromEntries(
		typeIds.map((typeId) => {
			const count = typeCounts.get(typeId) ?? 0;
			if (count === 0) return [typeId, "none" as ServerTypeState];
			return [
				typeId,
				count >= totalShipmentCount
					? ("all" as ServerTypeState)
					: ("partial" as ServerTypeState),
			];
		}),
	) as Record<number, ServerTypeState>;

const toSubscriberRow = (
	email: string,
	accumulator: RecipientAccumulator,
	totalShipmentCount: number,
	typeIds: readonly number[],
	targetScopes: readonly TargetScope[],
): SubscriberRow => {
	return {
		email,
		firstName: accumulator.firstName,
		lastName: accumulator.lastName,
		serverState: toServerState(
			accumulator.typeCounts,
			totalShipmentCount,
			typeIds,
		),
		scopes: targetScopes.map((targetScope) => {
			const scope = accumulator.scopes.get(targetScope.key);
			return {
				...targetScope,
				serverState: toServerState(
					scope?.typeCounts ?? new Map(),
					targetScope.targets.length,
					typeIds,
				),
				customerLevelTypeIds: scope?.customerLevelTypeIds ?? new Set<number>(),
			};
		}),
		isExisting: true,
	};
};

const getRecipientState = (
	recipients: readonly UnifiedIndividualNotificationRecipient[],
): Map<string, RecipientState> => {
	const byEmail = new Map<string, RecipientState>();
	for (const recipient of recipients) {
		const key = recipient.email.toLowerCase();
		const current = byEmail.get(key) ?? {
			firstName: recipient.firstName,
			lastName: recipient.lastName,
			effectiveTypeIds: new Set<number>(),
			customerLevelTypeIds: new Set<number>(),
			isCustomerLevel: false,
		};
		if (recipient.level === NotificationRecipientLevel.Customer) {
			current.isCustomerLevel = true;
		}
		for (const typeId of recipient.subscribedNotificationTypeIds) {
			current.effectiveTypeIds.add(typeId);
			if (recipient.level === NotificationRecipientLevel.Customer) {
				current.customerLevelTypeIds.add(typeId);
			}
		}
		byEmail.set(key, current);
	}
	return byEmail;
};

const aggregateRecipients = (
	snapshots: readonly BulkNotificationShipment[],
	targets: readonly BulkNotificationTarget[],
	typeIds: readonly number[],
): SubscriberRow[] => {
	const accumulators = new Map<string, RecipientAccumulator>();
	const targetScopes = groupTargetsByCustomer(targets);

	for (const snapshot of snapshots) {
		const scopeKey = getScopeKey(snapshot.customerId);
		const shipmentRecipients = getRecipientState(snapshot.recipients);
		for (const [email, state] of shipmentRecipients) {
			const accumulator =
				accumulators.get(email) ?? createRecipientAccumulator(state);
			mergeRecipientState(accumulator, state, scopeKey);
			accumulators.set(email, accumulator);
		}
	}

	return [...accumulators.entries()]
		.map(([email, accumulator]) =>
			toSubscriberRow(
				email,
				accumulator,
				targets.length,
				typeIds,
				targetScopes,
			),
		)
		.sort((left, right) => left.email.localeCompare(right.email));
};

const makeEmptyServerState = (
	typeIds: readonly number[],
): Record<number, ServerTypeState> =>
	Object.fromEntries(typeIds.map((typeId) => [typeId, "none"])) as Record<
		number,
		ServerTypeState
	>;

const createEmptySubscriberRow = (
	email: string,
	targetScopes: readonly TargetScope[],
	typeIds: readonly number[],
): SubscriberRow => ({
	email,
	firstName: null,
	lastName: null,
	serverState: makeEmptyServerState(typeIds),
	scopes: targetScopes.map((scope) => ({
		...scope,
		serverState: makeEmptyServerState(typeIds),
		customerLevelTypeIds: new Set<number>(),
	})),
	isExisting: false,
});

/** User edits layered over the server snapshot; absent entries mean "leave as is". */
type DraftRow = Record<number, boolean>;
type GetDraftValue = (
	row: SubscriberRow,
	typeId: number,
) => boolean | undefined;

const computeScopeDelta = (
	row: SubscriberRow,
	scope: SubscriberScope,
	typeIds: readonly number[],
	getDraftValue: GetDraftValue,
): { toAdd: number[]; toRemove: number[] } => {
	const toAdd: number[] = [];
	const toRemove: number[] = [];

	for (const typeId of typeIds) {
		if (scope.customerLevelTypeIds.has(typeId)) continue;
		const desired = getDraftValue(row, typeId);
		if (desired === undefined) continue;
		const current = scope.serverState[typeId] ?? "none";

		if (desired === true && current !== "all") toAdd.push(typeId);
		if (desired === false && current !== "none") toRemove.push(typeId);
	}

	return { toAdd, toRemove };
};

const computeSubscribedTypeIds = (
	scope: SubscriberScope,
	typeIds: readonly number[],
): number[] =>
	typeIds.filter(
		(typeId) =>
			!scope.customerLevelTypeIds.has(typeId) &&
			(scope.serverState[typeId] ?? "none") !== "none",
	);

const isTypeFullyLocked = (row: SubscriberRow, typeId: number): boolean =>
	row.scopes.length > 0 &&
	row.scopes.every((scope) => scope.customerLevelTypeIds.has(typeId));

const hasLockedScope = (row: SubscriberRow, typeId: number): boolean =>
	row.scopes.some((scope) => scope.customerLevelTypeIds.has(typeId));

const canRemoveSubscriber = (
	row: SubscriberRow,
	typeIds: readonly number[],
): boolean =>
	!row.isExisting ||
	row.scopes.some((scope) =>
		typeIds.some(
			(typeId) =>
				!scope.customerLevelTypeIds.has(typeId) &&
				(scope.serverState[typeId] ?? "none") !== "none",
		),
	);

const getOrCreateOperation = (
	operations: Map<string, ScopedBulkNotificationDelta>,
	scope: SubscriberScope,
): ScopedBulkNotificationDelta => {
	const operation = operations.get(scope.key) ?? {
		targets: scope.targets,
		subscribe: [],
		unsubscribe: [],
	};
	operations.set(scope.key, operation);
	return operation;
};

const appendSubscriberScopeDelta = (
	operations: Map<string, ScopedBulkNotificationDelta>,
	row: SubscriberRow,
	scope: SubscriberScope,
	typeIds: readonly number[],
	getDraftValue: GetDraftValue,
): void => {
	const { toAdd, toRemove } = computeScopeDelta(
		row,
		scope,
		typeIds,
		getDraftValue,
	);
	if (toAdd.length > 0) {
		getOrCreateOperation(operations, scope).subscribe.push({
			firstName: row.firstName,
			lastName: row.lastName,
			email: row.email,
			notificationTypeIds: toAdd,
		});
	}
	if (toRemove.length > 0) {
		getOrCreateOperation(operations, scope).unsubscribe.push({
			email: row.email,
			notificationTypeIds: toRemove,
		});
	}
};

const buildScopedDeltas = (
	subscribers: readonly SubscriberRow[],
	serverRows: readonly SubscriberRow[],
	removedEmails: readonly string[],
	typeIds: readonly number[],
	getDraftValue: GetDraftValue,
): ScopedBulkNotificationDelta[] => {
	const operations = new Map<string, ScopedBulkNotificationDelta>();

	for (const row of subscribers) {
		for (const scope of row.scopes) {
			appendSubscriberScopeDelta(
				operations,
				row,
				scope,
				typeIds,
				getDraftValue,
			);
		}
	}

	for (const email of removedEmails) {
		const row = serverRows.find(
			(candidate) => candidate.email.toLowerCase() === email.toLowerCase(),
		);
		if (!row) continue;
		for (const scope of row.scopes) {
			const toRemove = computeSubscribedTypeIds(scope, typeIds);
			if (toRemove.length === 0) continue;
			getOrCreateOperation(operations, scope).unsubscribe.push({
				email: row.email,
				notificationTypeIds: toRemove,
			});
		}
	}

	return [...operations.values()].filter(
		(operation) =>
			operation.subscribe.length > 0 || operation.unsubscribe.length > 0,
	);
};

export function BulkTenderNotificationModal({
	isOpen,
	onClose,
	targets,
}: BulkTenderNotificationModalProps) {
	const { email: currentUserEmail } = useIsAuthenticated();
	const { data: notificationTypes, isLoading: isLoadingTypes } =
		useNotificationTypes();
	const {
		data: snapshots,
		isLoading: isLoadingRecipients,
		isFetching: isFetchingRecipients,
	} = useBulkNotificationRecipients(targets);
	const saveMutation = useSaveBulkNotificationRecipients(targets);
	const [emailValues, setEmailValues] = useState<string[]>([]);
	const [filterValue, setFilterValue] = useState("");
	const [drafts, setDrafts] = useState<Record<string, DraftRow>>({});
	const [addedRows, setAddedRows] = useState<SubscriberRow[]>([]);
	const [removedEmails, setRemovedEmails] = useState<string[]>([]);
	const [emailError, setEmailError] = useState("");

	const typeIds = useMemo(
		() =>
			notificationTypes?.map((notificationType) => notificationType.id) ?? [],
		[notificationTypes],
	);
	const targetScopes = useMemo(
		() => groupTargetsByCustomer(targets),
		[targets],
	);

	const serverRows = useMemo(
		() => aggregateRecipients(snapshots ?? [], targets, typeIds),
		[snapshots, targets, typeIds],
	);

	const subscribers = useMemo(() => {
		const removed = new Set(removedEmails.map((email) => email.toLowerCase()));
		return [...serverRows, ...addedRows]
			.filter((row) => !removed.has(row.email.toLowerCase()))
			.sort((left, right) => left.email.localeCompare(right.email));
	}, [serverRows, addedRows, removedEmails]);

	const getDraftValue = useCallback(
		(row: SubscriberRow, typeId: number): boolean | undefined =>
			drafts[row.email.toLowerCase()]?.[typeId],
		[drafts],
	);

	const getEffectiveValue = useCallback(
		(row: SubscriberRow, typeId: number): boolean | null => {
			if (isTypeFullyLocked(row, typeId)) return true;
			const draft = getDraftValue(row, typeId);
			if (draft === true) return true;
			if (draft === false) return hasLockedScope(row, typeId) ? null : false;
			const state = row.serverState[typeId] ?? "none";
			if (state === "all") return true;
			return state === "partial" ? null : false;
		},
		[getDraftValue],
	);

	const isRowFullySelected = useCallback(
		(row: SubscriberRow) =>
			typeIds.length > 0 &&
			typeIds.every((typeId) => getEffectiveValue(row, typeId) === true),
		[getEffectiveValue, typeIds],
	);

	// Prefill the current user once the type list is known, unless already subscribed.
	const seededRef = useRef(false);
	useEffect(() => {
		if (seededRef.current || typeIds.length === 0 || snapshots === undefined) {
			return;
		}
		seededRef.current = true;
		if (
			currentUserEmail &&
			!serverRows.some(
				(row) => row.email.toLowerCase() === currentUserEmail.toLowerCase(),
			)
		) {
			setEmailValues([currentUserEmail]);
		}
	}, [currentUserEmail, serverRows, snapshots, typeIds.length]);

	const handleAddToSubscriptionList = () => {
		const pendingFilter = filterValue.trim();
		const allEmails = pendingFilter
			? [...emailValues, pendingFilter]
			: emailValues;
		if (allEmails.length === 0) return;

		if (allEmails.some((email) => email.length > MAX_EMAIL_LENGTH)) {
			setEmailError("Email must be 255 characters or fewer.");
			return;
		}
		const invalidEmails = allEmails.filter((email) => !isValidEmail(email));
		if (invalidEmails.length > 0) {
			setEmailError(
				`Invalid email address${invalidEmails.length > 1 ? "es" : ""}: ${invalidEmails.join(", ")}`,
			);
			return;
		}

		const uniqueNewEmails = [
			...new Map(
				allEmails.map((email) => [email.toLowerCase(), email]),
			).values(),
		];
		const existingEmails = new Set(
			subscribers.map((subscriber) => subscriber.email.toLowerCase()),
		);
		const duplicates = uniqueNewEmails.filter((email) =>
			existingEmails.has(email.toLowerCase()),
		);
		if (duplicates.length > 0) {
			setEmailError(`Already added: ${duplicates.join(", ")}`);
			return;
		}

		setEmailError("");
		setRemovedEmails((current) =>
			current.filter(
				(email) =>
					!uniqueNewEmails.some(
						(added) => added.toLowerCase() === email.toLowerCase(),
					),
			),
		);
		setAddedRows((current) => [
			...current,
			...uniqueNewEmails.map((email) =>
				createEmptySubscriberRow(email, targetScopes, typeIds),
			),
		]);
		setEmailValues([]);
		setFilterValue("");
	};

	const handleToggleType = useCallback(
		(row: SubscriberRow, typeId: number) => {
			if (isTypeFullyLocked(row, typeId)) return;
			const key = row.email.toLowerCase();
			const next = getEffectiveValue(row, typeId) !== true;
			setDrafts((current) => ({
				...current,
				[key]: { ...current[key], [typeId]: next },
			}));
		},
		[getEffectiveValue],
	);

	const handleToggleSelectAll = useCallback(
		(row: SubscriberRow) => {
			const key = row.email.toLowerCase();
			const next = !isRowFullySelected(row);
			const updates: DraftRow = {};
			for (const typeId of typeIds) {
				if (isTypeFullyLocked(row, typeId)) continue;
				updates[typeId] = next;
			}
			setDrafts((current) => ({
				...current,
				[key]: { ...current[key], ...updates },
			}));
		},
		[isRowFullySelected, typeIds],
	);

	const handleRemoveSubscriber = useCallback((row: SubscriberRow) => {
		const key = row.email.toLowerCase();
		setAddedRows((current) =>
			current.filter((added) => added.email.toLowerCase() !== key),
		);
		setDrafts((current) => {
			const next = { ...current };
			delete next[key];
			return next;
		});
		if (row.isExisting) {
			setRemovedEmails((current) =>
				current.some((email) => email.toLowerCase() === key)
					? current
					: [...current, row.email],
			);
		}
	}, []);

	const handleSubscribe = () => {
		const scopedDeltas = buildScopedDeltas(
			subscribers,
			serverRows,
			removedEmails,
			typeIds,
			getDraftValue,
		);
		if (scopedDeltas.length === 0) {
			onClose();
			return;
		}

		saveMutation.mutate(scopedDeltas, { onSuccess: onClose });
	};

	const columns = useMemo((): BoardColumn[] => {
		const emailCol: BoardColumn = {
			field: "email",
			title: "Email",
			show: true,
			filterable: false,
			sortable: false,
			editable: false,
			width: 200,
		};
		const selectAllCol: BoardColumn = {
			field: "selectAll",
			title: "Select all",
			show: true,
			filterable: false,
			sortable: false,
			width: 90,
			cells: {
				data: (props: GridCustomCellProps) => {
					const row = props.dataItem as SubscriberRow;
					const isFullyLocked = typeIds.every((typeId) =>
						isTypeFullyLocked(row, typeId),
					);
					return (
						<GridTd tdProps={props.tdProps}>
							<Checkbox
								disabled={isFullyLocked}
								onChange={() => handleToggleSelectAll(row)}
								value={isRowFullySelected(row)}
							/>
						</GridTd>
					);
				},
			},
		};
		const typeCols: BoardColumn[] = (notificationTypes ?? []).map(
			(notificationType: NotificationType) => ({
				field: `type_${String(notificationType.id)}`,
				title: notificationType.typeName.replace("Shipment", "").trim(),
				show: true,
				filterable: false,
				sortable: false,
				width: Math.max(
					notificationType.typeName.replace("Shipment", "").trim().length * 7 +
						10,
					80,
				),
				cells: {
					data: (props: GridCustomCellProps) => {
						const row = props.dataItem as SubscriberRow;
						// Only account-wide subscriptions are locked; shipment-level ones
						// stay editable so they can be turned off again.
						const isLocked = isTypeFullyLocked(row, notificationType.id);
						return (
							<GridTd tdProps={props.tdProps}>
								<Checkbox
									disabled={isLocked}
									onChange={() => handleToggleType(row, notificationType.id)}
									value={getEffectiveValue(row, notificationType.id)}
								/>
							</GridTd>
						);
					},
				},
			}),
		);
		const actionsCol: BoardColumn = {
			field: "actions",
			title: "Actions",
			show: true,
			filterable: false,
			sortable: false,
			width: 80,
			cells: {
				data: (props: GridCustomCellProps) => {
					const row = props.dataItem as SubscriberRow;
					return (
						<GridTd tdProps={props.tdProps}>
							<div className={styles.actionsCell}>
								{canRemoveSubscriber(row, typeIds) ? (
									<Button
										onClick={() => handleRemoveSubscriber(row)}
										svgIcon={trash_01}
										variant={"dangerTertiary"}
									/>
								) : null}
							</div>
						</GridTd>
					);
				},
			},
		};
		return [emailCol, selectAllCol, ...typeCols, actionsCol];
	}, [
		getEffectiveValue,
		handleRemoveSubscriber,
		handleToggleSelectAll,
		handleToggleType,
		isRowFullySelected,
		notificationTypes,
		typeIds,
	]);

	if (!isOpen) return null;

	const isLoading =
		isLoadingTypes || isLoadingRecipients || isFetchingRecipients;
	const shipmentCount = targets.length;

	return (
		<TraxModal
			className={styles.shipmentNotificationModal}
			onClose={onClose}
			title={"LTL Shipment Notifications"}
			width={"50%"}
		>
			<div className={styles.description}>
				<ul>
					<li>
						These preferences apply to all {shipmentCount} successfully tendered
						shipment{shipmentCount === 1 ? "" : "s"} in this bulk tender.
					</li>
					<li>
						Add an email address and click Add Subscriber to opt into shipment
						notifications.
					</li>
					<li>
						Select which notifications you would like to receive by email.
					</li>
					<li>
						Existing subscribers are already set to receive notifications for
						all shipments on this account. Their current event notifications
						can&apos;t be removed, but you may add additional event
						notifications if needed.
					</li>
					{shipmentCount > 1 ? (
						<li>
							A dash means the notification is active for only some of these
							shipments. Leave it as is to keep it that way, or click it to
							apply or clear it across all of them.
						</li>
					) : null}
				</ul>
			</div>

			<div className={styles.emailInputSection}>
				<span className={styles.emailInputLabel}>
					Enter the emails of new subscribers for these shipments:
				</span>
				<div className={styles.emailInputRow}>
					<MultiSelect
						allowCustom={true}
						autoClose={false}
						data={[]}
						filter={filterValue}
						filterable={true}
						listNoDataRender={() => null}
						onChange={(event: MultiSelectChangeEvent) => {
							setEmailValues(event.value as string[]);
							setFilterValue("");
							setEmailError("");
						}}
						onFilterChange={(event: MultiSelectFilterChangeEvent) => {
							setFilterValue(event.filter.value as string);
						}}
						placeholder={"Type an email and press Enter"}
						style={{ flex: 1, maxWidth: 500 }}
						tags={emailValues.map((email) => ({
							text: email,
							data: [email],
						}))}
						value={emailValues}
					/>
					<Button
						disabled={emailValues.length === 0 && !filterValue.trim()}
						onClick={handleAddToSubscriptionList}
						svgIcon={plusCircleLine}
						variant={"primary"}
					>
						Add Subscriber
					</Button>
				</div>
				{emailError ? (
					<span className={styles.emailError}>{emailError}</span>
				) : null}
			</div>

			<div className={styles.subscribersLabel}>
				Shipment Notification Subscribers:
			</div>
			<div className={styles.gridWrapper}>
				<TraxGrid
					columns={convertToGridColumnProps(columns)}
					data={subscribers}
					dataItemKey={"email"}
					loading={isLoading}
					loadingRows={5}
				/>
			</div>

			<DialogActionsBar layout={"end"}>
				<Button
					disabled={saveMutation.isPending}
					onClick={handleSubscribe}
					variant={"primary"}
				>
					Save Preferences
				</Button>
				<Button onClick={onClose} variant={"tertiary"}>
					Cancel
				</Button>
			</DialogActionsBar>
			<LoadingSpinner isVisible={saveMutation.isPending} />
		</TraxModal>
	);
}
